import { Person } from '@fs/zion-person'
import { Cell, colors, Grid, LayoutBand, Row, Separator, Skeleton, useContainerWidth } from '@fs/zion-ui'
import { css } from '@emotion/core'
import React from 'react'
import useQualityIndicatorFlag from '../../../data-service/useQualityIndicatorFlag'
import PersonQualityScoreButtonSkeleton from '../../../common/PersonQualityScoreButtonSkeleton'
import ColorStripe from './ColorStripe'
import IndicatorsRowSkeleton from './IndicatorsRowSkeleton'
import BannerActionsSkeleton from './BannerActionsSkeleton'

const marginOverrideCss = css`
  button,
  a {
    margin: 0;
  }
`

const alignCss = css`
  /* Start margin to line up the actions with the person details above it */
  margin-inline-start: var(--start-padding);
`

type Props = {
  isMobile: boolean
}

export default function PersonBannerSkeleton({ isMobile }: Props): JSX.Element {
  const { data: qualityIndicatorFlag } = useQualityIndicatorFlag()
  const showQualityScore = !!qualityIndicatorFlag?.isOn
  const atSize = useContainerWidth()
  const buttonSize = atSize({ default: 'xs', xs: 'sm', sm: 'md' })
  const componentSize = atSize({ default: 'sm', xxs: 'md', md: 'lg' })
  const smallMobile = atSize({ default: true, xs: false })
  const showActions = atSize({ default: false, sm: true })
  const avatarSize = atSize({ default: 'sm', sm: 'md', md: 'lg' })

  return (
    <div style={!isMobile ? { overflow: 'hidden' } : undefined}>
      <ColorStripe gender={undefined} />
      <LayoutBand maxWidth="lg" color={colors.gray00} marginY="nano" marginX="nano">
        {isMobile ? (
          <div style={{ minHeight: '64px' }}>
            <Row gutters="xxs">
              <Skeleton.Avatar alignY="middle" size={avatarSize} />
              <h1 alignY="middle">
                <Person loading hideAvatar componentSize={componentSize} lifespanSize="short" />
              </h1>

              <div growX />

              {!smallMobile && <IndicatorsRowSkeleton noShrinkX buttonSize={buttonSize} />}
              <Cell noShrinkX>
                <Row gutters="xxs">
                  {!showActions && (
                    <div noShrinkX css={marginOverrideCss}>
                      {/* eslint-disable-next-line test-selectors/button -- NA */}
                      <Skeleton.IconButton size={buttonSize} />
                    </div>
                  )}
                  <div noShrinkX css={marginOverrideCss}>
                    {/* eslint-disable-next-line test-selectors/button -- NA */}
                    <Skeleton.IconButton size={buttonSize} />
                  </div>
                </Row>

                {smallMobile && (
                  <Cell>
                    <Separator size="nano" />
                    <IndicatorsRowSkeleton buttonSize={buttonSize} />
                  </Cell>
                )}
              </Cell>
            </Row>
            <Grid gutters="nano">
              {showQualityScore && (
                <>
                  <Separator size={atSize({ default: 'xxs', xs: 'hide' })} />
                  <div
                    css={alignCss}
                    style={{
                      '--start-padding': atSize({
                        default: 'calc(40px + var(--spacer-xxs))',
                        sm: 'calc(64px + var(--spacer-xxs))',
                        md: 'calc(88px + var(--spacer-xxs))',
                      }),
                    }}
                  >
                    {/* eslint-disable-next-line test-selectors/button -- NA */}
                    <PersonQualityScoreButtonSkeleton variant="chip" />
                  </div>
                </>
              )}
              {showActions && (
                <>
                  <Separator size={atSize({ default: 'xxs', xs: 'hide' })} />
                  <div
                    css={alignCss}
                    style={{
                      '--start-padding': atSize({
                        default: 'calc(40px + var(--spacer-xxs) - 8px)',
                        sm: 'calc(64px + var(--spacer-xxs) - 8px)',
                        md: 'calc(88px + var(--spacer-xxs) - 8px)',
                      }),
                    }}
                  >
                    <BannerActionsSkeleton hideText={atSize({ default: true, md: false })} />
                  </div>
                </>
              )}
            </Grid>
          </div>
        ) : (
          <>
            <Row gutters="xxs" alignY="middle">
              <Skeleton.Avatar size="lg" />
              <h1>
                <Person loading hideAvatar componentSize="xl" lifespanSize="long" />
              </h1>
            </Row>
            <Grid gutters="nano">
              <div css={alignCss} style={{ '--start-padding': 'calc(88px + var(--spacer-xxs) - 8px)' }}>
                <BannerActionsSkeleton />
              </div>
            </Grid>
          </>
        )}
      </LayoutBand>
    </div>
  )
}
